/**
 * @fileoverview Optimization Target Constants
 * 
 * Centralized definitions for WFA optimization targets.
 * Using IDs allows display names to change without breaking functionality.
 */

export type OptimizationTargetId = 
  | 'sharpe_ratio'
  | 'total_return'
  | 'max_drawdown'
  | 'profit_factor'
  | 'win_rate';

export type MetricType = 'sharpeRatio' | 'totalReturn' | 'maxDrawdown' | 'winRate' | 'profitFactor';

export interface OptimizationTarget {
  id: OptimizationTargetId;
  displayName: string;
  metricKey: MetricType;
  description?: string;
}

export const OPTIMIZATION_TARGETS: OptimizationTarget[] = [
  {
    id: 'sharpe_ratio',
    displayName: 'Sharpe Ratio',
    metricKey: 'sharpeRatio',
    description: 'Risk-adjusted returns'
  },
  {
    id: 'total_return',
    displayName: 'Total Return (%)',
    metricKey: 'totalReturn',
    description: 'Maximize overall returns'
  },
  {
    id: 'profit_factor',
    displayName: 'Profit Factor',
    metricKey: 'profitFactor',
    description: 'Gross profit / Gross loss ratio'
  },
  {
    id: 'win_rate',
    displayName: 'Win Rate',
    metricKey: 'winRate',
    description: 'Percentage of winning trades'
  },
  {
    id: 'max_drawdown',
    displayName: 'Max Drawdown',
    metricKey: 'maxDrawdown',
    description: 'Minimize maximum loss from peak'
  }
];

// Helper functions
export const getOptimizationTargetById = (id: OptimizationTargetId): OptimizationTarget | undefined => {
  return OPTIMIZATION_TARGETS.find(target => target.id === id);
};

export const getMetricKeyFromTargetId = (id?: OptimizationTargetId | string): MetricType => {
  if (!id) return 'sharpeRatio';
  const target = OPTIMIZATION_TARGETS.find(t => t.id === id);
  return target?.metricKey || 'sharpeRatio';
};

export const getTargetIdFromMetricKey = (metricKey: MetricType): OptimizationTargetId => {
  const target = OPTIMIZATION_TARGETS.find(t => t.metricKey === metricKey);
  return target?.id || 'sharpe_ratio';
};

// Default optimization target
export const DEFAULT_OPTIMIZATION_TARGET: OptimizationTargetId = 'sharpe_ratio';
